<template>
  <div>
    <div class="reporting-settings-v2">
      <SavedReportForm
        class="m-b-10"
        :saved-report="activeReport"
        :report-type="reportType"
        :is-admin="isAdmin"
        @onReportNameChange="onReportNameChange"
        @onSave="saveReport"
        @onUpdate="saveReport"
        @onDelete="deleteReport"
        @onSaveAs="duplicateReport"
      />

      <div class="reporting-settings-v2__time">
        <TimeAgo
          :prefix-text="$t('UPDATED')"
          :from-date="lastUpdated"
          class="m-r-10 m-b-10"
        />

        <SearchableDropdown
          v-if="enableViewAs"
          name="V2ViewAsDropDown"
          :value="viewAs"
          :config="viewAsDropdownOptions"
          rules="required"
          class="reporting-settings-v2__timezone-selector m-b-10"
          :clearable="false"
          @input="setViewAs"
        >
          <template v-slot:option-data="{ option }">
            <div v-if="option.icon">
              <i
                class="view-as__option-heading typcn f-s-14"
                :class="option.icon"
              /><span v-text="option.name" />
            </div>
            <div 
              v-if="!option.icon"
              class="m-l-10"
              v-text="option.name" 
            />
          </template>
        </SearchableDropdown>

        <SearchableDropdown
          v-model="tempTimezoneId"
          rules="required"
          :config="timezoneDropdownOptions"
          class="reporting-settings-v2__timezone-selector m-b-10 m-l-10"
          :clearable="false"
        />
        
        <DateRangePicker
          date-range-button-class="reporting-settings-v2__date-picker btn m-l-10"
          class="m-b-10"
          :date-range="timeRange"
          :options="dateRangeConfig"
          :time-zone-id="translatedTimeZoneId"
          @onDateRangeChange="onDateRangeChange"
        />
      </div>
    
      <AutoReloadData 
        :auto-reload-enabled="autoReloadEnabled"
        class="reporting-settings-v2__auto-reload m-b-10 m-l-10"
        @toggleAutoReload="changeAutoReload"
        @loadData="refreshData"
      />

      <button
        v-if="!hideTourButton"
        v-tooltip.left="$t('START_TOUR')"
        class="btn m-l-10 m-b-10"
        @click="$emit('onStartTour')"
      >
        <i 
          class="fa fa-graduation-cap p-r-0"
        />
      </button>
    </div>

    <ReportingFilter
      :filter-criteria="filterCriteria"
      :available-filters="availableFilters"
      :form-name-for-user-settings="formNameForUserSettings"
      :event-category="reportType"
      :quick-filters="quickFilters"
      @applyFilters="onApplyFilters"
      @clearFilters="onClearFilters"
    />
  </div>
</template>

<script>
import moment from 'moment';
import 'moment-timezone';
import { mapState, mapActions, mapGetters } from 'vuex';
import { isEqual } from 'lodash';

import analyticsMixinFactory from '@/core/mixins/analyticsMixinFactory';
import sharedMessages from '@/core/locales';
import getDateRanges from '@/data/getDateRanges';
import SavedReportForm from '../SavedReportForm/SavedReportForm';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import DateRangePicker from '@/core/components/DateRangePicker/DateRangePicker';
import AutoReloadData from '@/core/components/AutoReloadData/AutoReloadData';
import ReportingFilter from '../ReportingFilter/ReportingFilter';
import TimeAgo from '@/core/components/TimeAgo/TimeAgo';

const AUTO_RELOAD_INTERVAL = 60 * 1000;

const DEFAULT_REPORTING_SETTINGS_EVENT_CATEGORY = 'reporting-settings';
const FILTERS_CLEAR_ACTION = 'filters-clear';
const FILTERS_APPLY_ACTION = 'filters-apply';
const DATE_RANGE_CHANGE_ACTION = 'daterange-change';
const AUTO_RELOAD_ENABLE_ACTION = 'autoreload-enable';
const AUTO_RELOAD_DISABLE_ACTION = 'autoreload-disable';
const REFRESH_DATA_ACTION = 'refresh-data';
const TIMEZONE_CHANGE_ACTION = 'timezone-change';


export default {
  name: 'ReportingSettings',
  components: {
    DateRangePicker,
    AutoReloadData,
    SavedReportForm,
    ReportingFilter,
    SearchableDropdown,
    TimeAgo,
  },
  mixins: [
    analyticsMixinFactory(DEFAULT_REPORTING_SETTINGS_EVENT_CATEGORY)
  ],
  props: {
    availableFilters: {
      type: Array,
      default() {
        return [];
      }
    },
    quickFilters: {
      type: Array,
      default() {
        return [];
      }
    },
    defaultTitle: {
      type: String,
      default: ''
    },
    activeReport: {
      type: Object,
      default() {
        return {};
      }
    },
    reportType: {
      type: String,
      default() {
        return (this.activeReport && this.activeReport.reportType) || '';
      }
    },
    timeZoneId: {
      type: String,
      default: ''
    },
    timeRange: {
      type: Array,
      default() {
        return [];
      }
    },
    translatedTimeZoneId: {
      type: String,
      default: ''
    },
    filterCriteria: {
      type: Array,
      default() {
        return [];
      }
    },
    lastUpdated: {
      type: Date,
      default() {
        return new Date();
      }
    },
    formNameForUserSettings: {
      type: String,
      default: ''
    },
    hideTourButton: {
      type: Boolean,
      default: false
    },
    viewAsPubManagers: {
      type: Array,
      default() {
        return [];
      }
    },
    viewAsBuyerManagers: {
      type: Array,
      default() {
        return [];
      }
    },
    viewAsPublishers: {
      type: Array,
      default() {
        return [];
      }
    },
    viewAsBuyers: {
      type: Array,
      default() {
        return [];
      }
    },
    viewAs: {
      type: Object,
      default() {
        return { icon: 'typcn-user', name: 'Admin', id: 'admin' };
      }
    }
  },
  data() {
    return {
      reportInterval: null
    };
  },

  provide() {
    return {
      reportType: this.reportType
    };
  },

  computed: {
    ...mapState([
      'timezones'
    ]),
    ...mapGetters(['isAdmin']),
    autoReloadEnabled() {
      return this.activeReport && this.activeReport.settings.autoReload;
    },
    dateRangeConfig() {
      const timezone = this.translatedTimeZoneId || moment.tz.guess();
      
      return {
        opens: 'left',
        timePicker: true,
        ranges: getDateRanges(timezone)
      };
    },
    timezoneDropdownOptions() {
      return {
        options: this.timezones,
        placeholder: 'Select timezone',
        getOptionLabel: function getOptionLabel(option) {
          return option && option.display;
        },
        reduce: option => option.id,
        clearable: false
      };
    },
    enableViewAs() {
      return this.viewAsPublishers?.length || this.viewAsBuyers?.length || this.viewAsPubManagers?.length || this.viewAsBuyerManagers?.length;
    },
    viewAsDropdownOptions() {
      if (!this.viewAsPublishers?.length && !this.viewAsBuyers?.length && !this.viewAsPubManagers?.length && !this.viewAsBuyerManagers?.length) {
        return null;
      }
      var options = [{ icon: 'typcn-user', name: 'Admin', id: 'admin' }];
      if (this.viewAsPublishers?.length) {
        options = [...options, { icon: 'typcn-group', name: 'Publishers'}, ...this.viewAsPublishers];
      }
      if (this.viewAsBuyers?.length) {
        options = [...options, { icon: 'typcn-eject-outline', name: 'Buyers'}, ...this.viewAsBuyers];
      }
      if (this.viewAsPubManagers?.length) {
        options = [...options, { icon: 'typcn-eject-outline', name: 'Publisher Managers'}, ...this.viewAsPubManagers];
      }
      if (this.viewAsBuyerManagers?.length) {
        options = [...options, { icon: 'typcn-eject-outline', name: 'Buyer Managers'}, ...this.viewAsBuyerManagers];
      }
      return {
        options,
        placeholder: 'View As',
        clearable: false,
        label: 'name'
      };
    },
    tempTimezoneId: {
      get() {
        return this.timeZoneId;
      },
      set(value) {
        if (this.timeZoneId !== value) {
          this.setReportingTimezoneId({ reportType: this.reportType, timeZoneId: value });
          this.refreshData();

          this.trackEvent(TIMEZONE_CHANGE_ACTION, undefined, this.reportType);
        }
      }
    },
  },
  watch: {
    activeReport: {
      immediate: true,
      handler(newValue) {
        // Set the document title to be the name of the insight
        if (newValue.id && newValue.name) {
          document.title = newValue.name;
        } else {
          document.title = newValue.name || this.defaultTitle;
        }

        if (this.autoReloadEnabled) {
          this.reloadDataInIntervals();
        }
      }
    }
  },
  mounted () {
    this.getTimezones();
  },
  destroyed () {
    this.clearReloadDataInterval();
    this.resetInsightSettings();
  },
  methods: {
    ...mapActions('reporting', [
      'setReportName',
      'setFilterCriteria',
      'setDateRange',
      'setAutoReload',
      'setReportingTimezoneId',
      'resetInsightSettings'
    ]),
    ...mapActions(['getTimezones']),
    onReportNameChange(name) {
      this.setReportName({ reportType: this.reportType, name });
    },
    saveReport(visibleAccountWide) {
      this.$emit('saveReport', visibleAccountWide);
      const isNew = !this.activeReport.id;

      this.trackEvent(`report-${isNew ? 'new-saved' : 'updated'}`, (visibleAccountWide ? 'acct-wide' : 'private'), this.reportType);
    },
    deleteReport() {
      this.$emit('deleteReport');

      this.trackEvent('report-deleted', 'report-deleted', this.reportType);
    },
    duplicateReport(visibleAccountWide) {
      this.$emit('duplicateReport', visibleAccountWide);

      this.trackEvent('report-duplicated', `visible-acct-wide-${visibleAccountWide}`, this.reportType);
    },
    onApplyFilters(filterCriteria) {
      this.setFilterCriteria({ reportType: this.reportType, filterCriteria });

      this.trackEvent(FILTERS_APPLY_ACTION, undefined, this.reportType);
    },
    onClearFilters(filterCriteria) {
      // Reload insights data only if the criteria set in Vuex is different from the new one
      if (!isEqual(filterCriteria, this.filterCriteria)) {
        this.onApplyFilters([]);
      }

      this.trackEvent(FILTERS_CLEAR_ACTION, undefined, this.reportType);
    },
    onDateRangeChange(dateRangeDetails) {
      this.setDateRange({ reportType: this.reportType, dateRangeDetails });
      this.refreshData();
      this.$emit('dateRangeChange');

      this.trackEvent(DATE_RANGE_CHANGE_ACTION, undefined, this.reportType);
    },
    changeAutoReload(autoReload) {
      this.setAutoReload({ reportType: this.reportType, autoReload });

      if (autoReload) {
        this.reloadDataInIntervals();
        this.trackEvent(AUTO_RELOAD_ENABLE_ACTION, undefined, this.reportType);
      } else {
        this.clearReloadDataInterval();
        this.trackEvent(AUTO_RELOAD_DISABLE_ACTION, undefined, this.reportType);
      }
    },
    reloadDataInIntervals() {
      this.clearReloadDataInterval();

      this.reportInterval = setInterval(() => {
        this.refreshData();
      }, AUTO_RELOAD_INTERVAL);
    },
    clearReloadDataInterval() {
      try {
        clearInterval(this.reportInterval);
      } catch (error) {
        //
      }
    },
    refreshData() {
      this.$emit('refreshData');
      this.trackEvent(REFRESH_DATA_ACTION, undefined, this.reportType);
    },
    setViewAs(option) {
      if(option?.id) {
        this.$emit('viewAsChange', option);
      }
    }
  },
  i18n: {
    locale: 'en',
    sharedMessages
  },
};
</script>

<style lang="scss">
.reporting-settings-v2 {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.reporting-settings-v2__time-range {
  background: var(--component-widget-panel__holder-bg);
}

.reporting-settings-v2__date-picker {
  .caret {
    margin: 0 0 3px 5px;
  }
}

.reporting-settings-v2__time {
  display: flex;
  align-items: center;
  margin-left: auto;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.reporting-settings-v2__timezone-selector, ul.vs__dropdown-menu {
  // TODO: Move this to a mixin
  &::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }
  &::-webkit-scrollbar-track, &::-webkit-scrollbar-corner {
    background-color: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--component-grid-scroll-thumb-color);
  }
  scrollbar-color: var(--component-grid-scroll-thumb-color) transparent;
  scrollbar-width: thin;
}

.reporting-settings-v2__timezone-selector {
  width: $reporting-settings-v2-timezone-selector-width;
}
</style>